// @ts-strict-ignore
import React, { useMemo, useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { BodyText, Button, FieldLabel, FlexBox, Link, StatusText } from "@braze/beacon-core";
import { CheckboxField } from "@braze/beacon-forms";
import { yupResolver } from "@braze/hookform-resolvers-legacy/yup";
import { useI18nLocale } from "@braze/i18n-core";
import * as Yup from "@braze/yup-legacy";

import useI18nNamespace from "@services/useI18nNamespace";

import { SelectField } from "@components/ReactHookForm";

import { FormHeaderText } from "../FormHeaderText";
import { countrySchema } from "../utils";
import { StyledForm } from "./BusinessDetails.style";
import { fieldOptions } from "./fieldOptions";
import type { BusinessDetailsProps, BusinessDetailsType } from "./types";

const hasChannelMessagingFeature = (features?: string[]): boolean =>
  features?.includes("Cross-Channel Messaging") ?? false;

export const BusinessDetails = ({
  businessDetails,
  onSubmit,
  isLoading,
  errorMessage,
}: BusinessDetailsProps): React.ReactElement => {
  const currentLocale = useI18nLocale();
  const { t } = useI18nNamespace("free-trial-registration");
  const { t: l } = useI18nNamespace("country-labels");
  const validationSchema = useMemo(() => {
    return Yup.object({
      industry: Yup.string().required(t("industry-required-error-text", "Industry is required")),
      companySize: Yup.string().required(t("company-size-required-error-text", "Company size is required")),
      country: countrySchema(t),
      usersCount: Yup.string().required(t("users-count-required-error-text", "User count is required")),
      features: Yup.array(Yup.string()).min(
        1,
        t("features-required-error-text", "At least one feature is required")
      ),
      goals: Yup.array(Yup.string()).min(1, t("goals-required-error-text", "At least one goal is required")),
      channels: Yup.array(Yup.string()).when("features", {
        is: hasChannelMessagingFeature,
        then: () =>
          Yup.array(Yup.string()).min(1, t("channels-required-error-text", "At least one channel is required")),
        otherwise: () => Yup.array(Yup.string()),
      }),
    });
  }, [t]);

  const form = useForm<BusinessDetailsType>({
    defaultValues: businessDetails,
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
  });

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const selectedFeatures = useWatch({
    control,
    name: "features",
    defaultValue: businessDetails.features,
  });

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "features") {
        if (!hasChannelMessagingFeature(value.features)) {
          setValue("channels", []);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const options = useMemo(() => fieldOptions(t, l, currentLocale), [t, l, currentLocale]);
  const marketingWebLocale = currentLocale === "en" ? "en-us" : currentLocale;

  return (
    <StyledForm data-cy="businessDetailsForm" id="businessDetailsForm" onSubmit={handleSubmit(onSubmit)}>
      <FormProvider {...form}>
        <FormHeaderText />
        <FieldLabel
          label={t("industry-selection-label", "Industry")}
          status="danger"
          statusText={errors.industry?.message}
        >
          <SelectField
            id="industry"
            name="industry"
            placeholder={t("industry-placeholder", "Select your industry")}
            options={options.industry}
            fluid
            isClearable={false}
            control={control}
          />
        </FieldLabel>
        <FieldLabel
          label={t("company-size-selection-label", "Company size")}
          status="danger"
          statusText={errors.companySize?.message}
        >
          <SelectField
            id="companySize"
            name="companySize"
            placeholder={t("company-size-placeholder", "Select size range")}
            options={options.companySize}
            fluid
            isClearable={false}
            control={control}
          />
        </FieldLabel>
        <FieldLabel
          label={t("country-selection-label", "Country")}
          status="danger"
          statusText={errors.country?.message}
        >
          <SelectField
            id="country"
            name="country"
            placeholder={t("country-placeholder", "Select country")}
            options={options.country}
            fluid
            isClearable={false}
            control={control}
            menuPortalTarget={document.body}
          />
        </FieldLabel>
        <FieldLabel
          label={t("user-count-label", "How many contacts or users do you have?")}
          status="danger"
          statusText={errors.usersCount?.message}
        >
          <SelectField
            id="usersCount"
            name="usersCount"
            placeholder={t("user-count-placeholder", "Select user range")}
            options={options.usersCount}
            fluid
            isClearable={false}
            control={control}
          />
        </FieldLabel>
        <FieldLabel
          label={t("features-selection-label", "Which Braze features are you interested in?")}
          status="danger"
          statusText={errors.features?.message}
        >
          <SelectField
            id="features"
            name="features"
            placeholder={t("features-selection-placeholder", "Select features")}
            options={options.features}
            fluid
            isClearable={false}
            control={control}
            isMulti
            blurInputOnSelect={false}
            menuPortalTarget={document.body}
          />
        </FieldLabel>
        {hasChannelMessagingFeature(selectedFeatures) && (
          <FieldLabel
            label={t("channel-selection-label", "Which messaging channels are you interested in?")}
            status="danger"
            statusText={errors.channels?.message}
          >
            <SelectField
              id="channels"
              name="channels"
              placeholder={t("channel-selection-placeholder", "Select channels")}
              options={options.channels}
              fluid
              isClearable={false}
              control={control}
              isMulti
              blurInputOnSelect={false}
              menuPortalTarget={document.body}
            />
          </FieldLabel>
        )}
        <FieldLabel
          label={t("goal-selection-label", "What can Braze do for your business?")}
          status="danger"
          statusText={errors.goals?.message}
        >
          <SelectField
            id="goals"
            name="goals"
            placeholder={t("goal-selection-placeholder", "Select goals")}
            options={options.goals}
            fluid
            isClearable={false}
            control={control}
            isMulti
            blurInputOnSelect={false}
            menuPortalTarget={document.body}
          />
        </FieldLabel>
        <BodyText>
          <em>
            {t(
              "privacy-policy-acknowledgment",
              "By submitting this form, you acknowledge that Braze will process your personal data in accordance with our"
            )}{" "}
            <Link
              showExternalLinkIcon
              href={`https://www.braze.com/${marketingWebLocale}/company/legal/privacy`}
              target="_blank"
            >
              {t("privacy-policy-link-text", "Privacy Policy")}
            </Link>
          </em>
        </BodyText>
        <FlexBox my="lg" direction="column">
          <CheckboxField
            control={control}
            checkboxLabelAlignment="top"
            name="optInMarketing"
            label={t(
              "opt-in-checkbox-label",
              "Opt in to receive marketing tips, insights, invitations, product updates, targeted advertising and more from Team Braze. Please see the Privacy Policy for more information."
            )}
          />
        </FlexBox>
      </FormProvider>
      <Button size="full" type="submit" loadingState={isLoading ? "loading" : "idle"}>
        {t("business-details-continue-button-text", "Continue")}
      </Button>
      {errorMessage && <StatusText status="danger">{errorMessage}</StatusText>}
    </StyledForm>
  );
};
